import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fileToResizedDataUrl } from "@/lib/image";
import AmmTabs from "@/components/AmmTabs";
import PreventivoDoc from "@/components/docs/PreventivoDoc";
import { exportElementToPdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Receipt, Trash2, Pencil, Download, Copy, X, Eye, Image as ImageIcon, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "bozza", label: "Bozza", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-400/30" },
  { key: "inviato", label: "Inviato", cls: "bg-blue-500/15 text-blue-300 border-blue-400/30" },
  { key: "accettato", label: "Accettato", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" },
  { key: "rifiutato", label: "Rifiutato", cls: "bg-red-500/15 text-red-300 border-red-400/30" },
];

const parseAmount = (s) => {
  if (!s) return 0;
  const cleaned = String(s).replace(/[^\d.,]/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".");
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

const SERVICE_OPTIONS = [
  "Gestione Social Media", "Gestione Grafiche", "Piano Editoriale",
  "Campagne ADV (Meta/Google)", "Shooting Foto", "Shooting Video / Reel",
  "Video Editing", "Sito Web / Landing Page", "Consulenza Strategica",
];

const fmtDateLabel = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("T")[0].split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
};
const fmtCreated = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const emptyItem = () => ({ title: "", description: "", price: "", unit: "/ mese" });
const emptyTranche = () => ({ date: "", amount: "", label: "" });
const todayInput = () => new Date().toISOString().split("T")[0];

const emptyForm = () => ({
  id: null,
  client_name: "",
  project: "",
  date: todayInput(),
  period: "",
  package_name: "",
  package_price: "",
  package_unit: "/ mese",
  items: [emptyItem()],
  total_label: "TOTALE SEMESTRALE",
  total_amount: "",
  payment_label: "PAGAMENTO TRIMESTRALE ANTICIPATO",
  tranches: [emptyTranche()],
  note: "Fattura per ogni tranche. Pagamento entro 15 giorni dall'emissione.",
  status: "bozza",
});

const toDocData = (f) => ({
  client_name: f.client_name,
  project: f.project,
  date_label: fmtDateLabel(f.date),
  period: f.period,
  package_name: f.package_name,
  package_price: f.package_price,
  package_unit: f.package_unit,
  items: (f.items || []).filter((it) => it.title || it.price),
  total_label: f.total_label,
  total_amount: f.total_amount,
  payment_label: f.payment_label,
  tranches: (f.tranches || []).filter((t) => t.date || t.amount),
  note: f.note,
});

export default function AmmPreventivi() {
  const { clients, updateClient } = useData();
  const adminClient = clients.find((c) => c.id === "admin-section");
  const logoUrl = adminClient?.logo_url || null;
  const logoInputRef = useRef(null);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [docData, setDocData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const pdfRef = useRef(null);

  // Upload PDF esterno
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ client_name: "", file_name: "", file: null });
  const [uploading, setUploading] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);

  const fetchBlobUrl = async (p) => {
    const { data } = await api.get(`/preventivi/${p.id}/file`, { responseType: "blob" });
    return URL.createObjectURL(data);
  };

  const submitUpload = async () => {
    if (!uploadForm.file) return toast.error("Seleziona un file PDF");
    if (!uploadForm.client_name.trim()) return toast.error("Inserisci il nome cliente");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("client_name", uploadForm.client_name);
      fd.append("file_name", uploadForm.file_name || uploadForm.file.name);
      await api.post("/preventivi/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadOpen(false);
      setUploadForm({ client_name: "", file_name: "", file: null });
      toast.success("PDF caricato");
      load();
    } catch (e) { toast.error("Errore caricamento PDF"); }
    finally { setUploading(false); }
  };

  const openFilePreview = async (p) => {
    try {
      const url = await fetchBlobUrl(p);
      setFilePreviewUrl(url);
      setFilePreviewOpen(true);
    } catch { toast.error("Errore apertura PDF"); }
  };

  const downloadFile = async (p) => {
    try {
      const url = await fetchBlobUrl(p);
      const a = document.createElement("a");
      a.href = url;
      a.download = p.file_name || `${p.client_name || "documento"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Errore download PDF"); }
  };

  const openRename = (p) => { setRenameTarget(p); setRenameValue((p.file_name || "").replace(/\.pdf$/i, "")); setRenameOpen(true); };
  const submitRename = async () => {
    if (!renameValue.trim()) return;
    try {
      await api.patch(`/preventivi/${renameTarget.id}/meta`, { file_name: renameValue.trim() });
      setRenameOpen(false);
      toast.success("Rinominato");
      load();
    } catch { toast.error("Errore rinomina"); }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Immagine troppo grande (max 4MB)");
    try {
      const url = await fileToResizedDataUrl(file, 600, 0.9);
      await updateClient("admin-section", { logo_url: url });
      toast.success("Logo agenzia aggiornato");
    } catch { toast.error("Errore caricamento logo"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/preventivi");
      setList(data);
    } catch { toast.error("Errore caricamento preventivi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      (p.client_name || "").toLowerCase().includes(q) ||
      (p.project || "").toLowerCase().includes(q) ||
      (p.file_name || "").toLowerCase().includes(q) ||
      (p.number || "").toLowerCase().includes(q));
  }, [list, search]);

  const openNew = () => { setForm(emptyForm()); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      ...emptyForm(), ...p,
      date: (p.date || todayInput()).split("T")[0],
      items: p.items?.length ? p.items : [emptyItem()],
      tranches: p.tranches?.length ? p.tranches : [emptyTranche()],
    });
    setOpen(true);
  };

  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const setTranche = (i, patch) => setForm((f) => ({ ...f, tranches: f.tranches.map((t, idx) => idx === i ? { ...t, ...patch } : t) }));
  const addTranche = () => setForm((f) => ({ ...f, tranches: [...f.tranches, emptyTranche()] }));
  const removeTranche = (i) => setForm((f) => ({ ...f, tranches: f.tranches.filter((_, idx) => idx !== i) }));

  const payload = (f) => ({
    client_name: f.client_name, project: f.project, date: f.date || null, period: f.period,
    package_name: f.package_name, package_price: f.package_price,
    items: (f.items || []).map((it) => ({ title: it.title, description: it.description, price: it.price, unit: it.unit })),
    total_label: f.total_label, total_amount: f.total_amount,
    payment_label: f.payment_label, tranches: f.tranches || [], note: f.note, status: f.status,
    package_unit: f.package_unit,
  });

  const save = async () => {
    if (!form.client_name.trim()) return toast.error("Inserisci il nome cliente");
    setSaving(true);
    try {
      if (form.id) await api.patch(`/preventivi/${form.id}`, payload(form));
      else await api.post("/preventivi", payload(form));
      setOpen(false);
      toast.success("Preventivo salvato");
      load();
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questo preventivo?")) return;
    await api.delete(`/preventivi/${id}`);
    toast.success("Preventivo eliminato");
    load();
  };

  const duplicate = async (p) => {
    const { id, number, created_at, updated_at, ...rest } = p;
    await api.post("/preventivi", { ...payload({ ...emptyForm(), ...rest }), client_name: `${p.client_name} (copia)` });
    toast.success("Preventivo duplicato");
    load();
  };

  const downloadPdf = (p) => {
    const f = { ...emptyForm(), ...p, date: (p.date || "").split("T")[0] };
    setDocData(toDocData(f));
    setTimeout(async () => {
      try {
        await exportElementToPdf(pdfRef.current, `Preventivo-${(p.client_name || "cliente").replace(/\s+/g, "_")}.pdf`);
      } catch { toast.error("Errore generazione PDF"); }
      setDocData(null);
    }, 400);
  };

  const openPreview = (p) => {
    const f = { ...emptyForm(), ...p, date: (p.date || "").split("T")[0] };
    setPreviewData(toDocData(f));
    setPreviewOpen(true);
  };

  const changeStatus = async (p, status) => {
    try {
      if (p.kind === "upload") {
        await api.patch(`/preventivi/${p.id}/meta`, { status });
      } else {
        await api.patch(`/preventivi/${p.id}`, { ...payload({ ...emptyForm(), ...p }), status });
      }
      setList((prev) => prev.map((x) => (x.id === p.id ? { ...x, status } : x)));
    } catch { toast.error("Errore aggiornamento stato"); }
  };

  // Totale accettato del mese corrente (per data documento, altrimenti data creazione)
  const acceptedThisMonth = useMemo(() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return list
      .filter((p) => p.status === "accettato")
      .filter((p) => ((p.date || p.created_at || "").slice(0, 7)) === ym)
      .reduce((sum, p) => sum + parseAmount(p.total_amount), 0);
  }, [list]);

  const downloadPdfFromDoc = (dd) => {
    setDocData(dd);
    setTimeout(async () => {
      try {
        await exportElementToPdf(pdfRef.current, `Preventivo-${(dd.client_name || "cliente").replace(/\s+/g, "_")}.pdf`);
      } catch { toast.error("Errore generazione PDF"); }
      setDocData(null);
    }, 400);
  };

  const inputCls = "mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-fuchsia-500 outline-none";

  return (
    <div className="space-y-6 tp-fade-in">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Interna</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">Amministrazione</h1>
      </div>
      <AmmTabs />

      <section className="tp-glass rounded-3xl overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-white/5 flex items-center gap-3 flex-wrap">
          <Receipt size={15} className="text-fuchsia-400" />
          <h2 className="text-base font-semibold text-white">Preventivi</h2>
          <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30" data-testid="accepted-month-total">
            Accettato questo mese: € {acceptedThisMonth.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          </span>
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="logo-input" />
          <button onClick={() => logoInputRef.current?.click()} data-testid="logo-upload-button"
            className="ml-auto flex items-center gap-1.5 text-xs px-3 h-9 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5">
            <ImageIcon size={14} /> {logoUrl ? "Cambia logo" : "Logo agenzia"}
          </button>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="preventivi-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca cliente o progetto..." className="pl-9 h-9 w-56 bg-white/5 border-white/10 text-white" />
          </div>
          <Button data-testid="upload-preventivo-button" onClick={() => setUploadOpen(true)} variant="outline"
            className="h-9 rounded-full px-3 border-white/10 text-zinc-300 hover:bg-white/5">
            <Upload size={14} className="mr-1.5" /> Carica PDF
          </Button>
          <Button data-testid="add-preventivo-button" onClick={openNew} className="tp-brand-btn h-9 rounded-full px-3">
            <Plus size={14} className="mr-1.5" /> Preventivo
          </Button>
        </div>
        <div className="p-4 space-y-2">
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-zinc-500 italic">Nessun preventivo. Creane uno con "Preventivo".</div>
          ) : (
            filtered.map((p) => (
              <div key={p.id} data-testid={`preventivo-row-${p.id}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${p.kind === "upload" ? "bg-white/10 border border-white/10" : "tp-brand-gradient"}`}>
                  {p.kind === "upload" ? <FileText size={16} className="text-fuchsia-300" /> : <Receipt size={16} className="text-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium truncate flex items-center gap-2">
                    {p.kind === "upload" ? (p.file_name || p.client_name) : p.client_name}
                    {p.kind === "upload" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-400/20">PDF</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{p.number} · {p.kind === "upload" ? p.client_name : (p.project || "—")}</div>
                </div>
                <div className="text-xs text-zinc-400 hidden sm:block whitespace-nowrap">Creato: {fmtCreated(p.created_at)}</div>
                <select
                  value={p.status || "bozza"}
                  onChange={(e) => changeStatus(p, e.target.value)}
                  data-testid={`preventivo-status-${p.id}`}
                  className={`text-xs rounded-full border px-2 py-1 outline-none cursor-pointer bg-transparent ${(STATUSES.find((s) => s.key === (p.status || "bozza")) || STATUSES[0]).cls}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s.key} value={s.key} className="bg-zinc-900 text-white">{s.label}</option>
                  ))}
                </select>
                {p.kind === "upload" ? (
                  <>
                    <button onClick={() => openFilePreview(p)} title="Anteprima" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`preventivo-preview-${p.id}`}><Eye size={15} /></button>
                    <button onClick={() => downloadFile(p)} title="Scarica PDF" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`preventivo-pdf-${p.id}`}><Download size={15} /></button>
                    <button onClick={() => openRename(p)} title="Rinomina" className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5" data-testid={`preventivo-rename-${p.id}`}><Pencil size={14} /></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => openPreview(p)} title="Anteprima" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`preventivo-preview-${p.id}`}><Eye size={15} /></button>
                    <button onClick={() => downloadPdf(p)} title="Scarica PDF" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`preventivo-pdf-${p.id}`}><Download size={15} /></button>
                    <button onClick={() => duplicate(p)} title="Duplica" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`preventivo-duplicate-${p.id}`}><Copy size={14} /></button>
                    <button onClick={() => openEdit(p)} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5" data-testid={`preventivo-edit-${p.id}`}><Pencil size={14} /></button>
                  </>
                )}
                <button onClick={() => remove(p.id)} className="p-2 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-500/10" data-testid={`preventivo-delete-${p.id}`}><Trash2 size={14} /></button>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Light editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-0 tp-glass-strong text-white border-white/10 max-h-[92vh] overflow-y-auto" data-testid="preventivo-editor">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
            <DialogTitle className="text-white flex items-center gap-2">
              <Receipt size={18} className="text-fuchsia-400" /> {form.id ? "Modifica preventivo" : "Nuovo preventivo"}
            </DialogTitle>
          </DialogHeader>

          <div className="px-6 py-5 space-y-6">
            {/* Intestazione */}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm"><span className="text-xs text-zinc-500">Cliente</span>
                <input data-testid="preventivo-client" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} className={inputCls} placeholder="Es. Atletica Sambenedettese" /></label>
              <label className="text-sm"><span className="text-xs text-zinc-500">Progetto / sottotitolo</span>
                <input value={form.project} onChange={(e) => setForm((f) => ({ ...f, project: e.target.value }))} className={inputCls} placeholder="Es. Piano semestrale di comunicazione" /></label>
              <label className="text-sm"><span className="text-xs text-zinc-500">Data documento</span>
                <input type="date" value={form.date || ""} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} /></label>
              <label className="text-sm"><span className="text-xs text-zinc-500">Periodo (testo)</span>
                <input value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))} className={inputCls} placeholder="1 settembre 2025 — 28 febbraio 2026" /></label>
            </div>

            {/* Pacchetto */}
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="text-sm sm:col-span-1"><span className="text-xs text-zinc-500">Pacchetto (opzionale)</span>
                <input value={form.package_name} onChange={(e) => setForm((f) => ({ ...f, package_name: e.target.value }))} className={inputCls} placeholder="Es. Pacchetto Base" /></label>
              <label className="text-sm"><span className="text-xs text-zinc-500">Prezzo pacchetto</span>
                <input value={form.package_price} onChange={(e) => setForm((f) => ({ ...f, package_price: e.target.value }))} className={inputCls} placeholder="€400" /></label>
              <label className="text-sm"><span className="text-xs text-zinc-500">Unità</span>
                <input value={form.package_unit} onChange={(e) => setForm((f) => ({ ...f, package_unit: e.target.value }))} className={inputCls} placeholder="/ mese" /></label>
            </div>

            {/* Voci */}
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Servizi</div>
              <div className="space-y-3">
                {form.items.map((it, i) => (
                  <div key={i} className="rounded-xl border border-white/10 p-3 space-y-2">
                    <div className="flex gap-2">
                      <input list="svc-opts" data-testid={`item-service-${i}`} value={it.title} onChange={(e) => setItem(i, { title: e.target.value })}
                        className="flex-1 rounded-md border border-white/10 bg-white/5 text-white px-2 py-1.5 text-sm font-medium outline-none focus:border-fuchsia-500" placeholder="Servizio (es. Gestione Social Media)" />
                      <input value={it.price} onChange={(e) => setItem(i, { price: e.target.value })}
                        className="w-24 rounded-md border border-white/10 bg-white/5 text-white px-2 py-1.5 text-sm text-right outline-none focus:border-fuchsia-500" placeholder="€350" />
                      <input value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })}
                        className="w-20 rounded-md border border-white/10 bg-white/5 text-white px-2 py-1.5 text-sm outline-none focus:border-fuchsia-500" placeholder="/ mese" />
                      <button onClick={() => removeItem(i)} className="text-zinc-400 hover:text-red-500 px-1"><X size={16} /></button>
                    </div>
                    <textarea value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} rows={2}
                      className="w-full rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-zinc-300 outline-none focus:border-fuchsia-500 resize-none" placeholder="Descrizione (una riga per dettaglio)" />
                  </div>
                ))}
                <datalist id="svc-opts">{SERVICE_OPTIONS.map((s) => <option key={s} value={s} />)}</datalist>
                <button onClick={addItem} data-testid="preventivo-add-item" className="w-full rounded-lg border border-dashed border-fuchsia-300 py-2 text-sm text-fuchsia-400 hover:bg-fuchsia-500/10 flex items-center justify-center gap-1">
                  <Plus size={14} /> Aggiungi servizio
                </button>
              </div>
            </div>

            {/* Totale */}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm"><span className="text-xs text-zinc-500">Etichetta totale</span>
                <input value={form.total_label} onChange={(e) => setForm((f) => ({ ...f, total_label: e.target.value }))} className={inputCls} placeholder="TOTALE SEMESTRALE" /></label>
              <label className="text-sm"><span className="text-xs text-zinc-500">Importo totale</span>
                <input value={form.total_amount} onChange={(e) => setForm((f) => ({ ...f, total_amount: e.target.value }))} className={inputCls} placeholder="€2.400" /></label>
            </div>

            {/* Pagamento */}
            <div>
              <label className="text-sm block"><span className="text-xs text-zinc-500">Modalità di pagamento</span>
                <input value={form.payment_label} onChange={(e) => setForm((f) => ({ ...f, payment_label: e.target.value }))} className={inputCls} placeholder="PAGAMENTO TRIMESTRALE ANTICIPATO" /></label>
              <div className="mt-3 space-y-2">
                {form.tranches.map((t, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={t.date} onChange={(e) => setTranche(i, { date: e.target.value })} className="flex-1 rounded-md border border-white/10 bg-white/5 text-white px-2 py-1.5 text-sm outline-none focus:border-fuchsia-500" placeholder="1 settembre 2025" />
                    <input value={t.amount} onChange={(e) => setTranche(i, { amount: e.target.value })} className="w-28 rounded-md border border-white/10 bg-white/5 text-white px-2 py-1.5 text-sm outline-none focus:border-fuchsia-500" placeholder="€1.200" />
                    <input value={t.label} onChange={(e) => setTranche(i, { label: e.target.value })} className="w-32 rounded-md border border-white/10 bg-white/5 text-white px-2 py-1.5 text-sm outline-none focus:border-fuchsia-500" placeholder="Alla firma" />
                    <button onClick={() => removeTranche(i)} className="text-zinc-400 hover:text-red-500 px-1"><X size={16} /></button>
                  </div>
                ))}
                <button onClick={addTranche} className="text-sm text-fuchsia-400 hover:underline flex items-center gap-1"><Plus size={13} /> Aggiungi tranche</button>
              </div>
            </div>

            <label className="text-sm block"><span className="text-xs text-zinc-500">Nota</span>
              <input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className={inputCls} /></label>
          </div>

          <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2 sticky bottom-0 tp-glass-strong">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10 text-zinc-200">Annulla</Button>
            <Button variant="outline" onClick={() => openPreview(form)} className="border-white/10 text-zinc-200" data-testid="preventivo-editor-preview"><Eye size={15} className="mr-1.5" /> Anteprima</Button>
            {form.id ? (
              <Button variant="outline" onClick={() => downloadPdf(form)} className="border-fuchsia-300 text-fuchsia-400" data-testid="preventivo-editor-pdf"><Download size={15} className="mr-1.5" /> PDF</Button>
            ) : null}
            <Button onClick={save} disabled={saving} data-testid="preventivo-save" className="tp-brand-btn">{saving ? "Salvo…" : "Salva preventivo"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Anteprima documento a schermo */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[860px] p-0 bg-zinc-100 border-zinc-300 max-h-[92vh] overflow-y-auto" data-testid="preventivo-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10 flex-row items-center justify-between space-y-0 sticky top-0 bg-zinc-100 z-10">
            <DialogTitle className="text-white flex items-center gap-2"><Eye size={18} className="text-fuchsia-400" /> Anteprima preventivo</DialogTitle>
            <button onClick={() => previewData && downloadPdfFromDoc(previewData)} className="text-sm text-fuchsia-400 hover:underline flex items-center gap-1" data-testid="preview-download"><Download size={14} /> Scarica PDF</button>
          </DialogHeader>
          <div className="p-5 flex justify-center">
            <div style={{ width: 794, transformOrigin: "top center" }} className="shadow-xl">
              {previewData && <PreventivoDoc data={previewData} logoUrl={logoUrl} />}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Off-screen render target for PDF export */}
      <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden="true">
        <div ref={pdfRef}>{docData && <PreventivoDoc data={docData} logoUrl={logoUrl} />}</div>
      </div>

      {/* Upload PDF esterno */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md p-0 tp-glass-strong text-white border-white/10" data-testid="preventivo-upload-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
            <DialogTitle className="text-white flex items-center gap-2"><Upload size={18} className="text-fuchsia-400" /> Carica preventivo (PDF)</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-5 space-y-4">
            <label className="text-sm block"><span className="text-xs text-zinc-500">Cliente</span>
              <input data-testid="upload-client" value={uploadForm.client_name} onChange={(e) => setUploadForm((f) => ({ ...f, client_name: e.target.value }))} className={inputCls} placeholder="Nome cliente" /></label>
            <label className="text-sm block"><span className="text-xs text-zinc-500">Nome file (opzionale)</span>
              <input data-testid="upload-name" value={uploadForm.file_name} onChange={(e) => setUploadForm((f) => ({ ...f, file_name: e.target.value }))} className={inputCls} placeholder="Es. Preventivo Luglio" /></label>
            <label className="text-sm block"><span className="text-xs text-zinc-500">File PDF</span>
              <input data-testid="upload-file" type="file" accept="application/pdf" onChange={(e) => setUploadForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
                className="mt-1 w-full text-sm text-zinc-700 file:mr-3 file:rounded-lg file:border-0 file:bg-fuchsia-50 file:px-3 file:py-2 file:text-fuchsia-700 hover:file:bg-fuchsia-100" /></label>
          </div>
          <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="border-white/10 text-zinc-200">Annulla</Button>
            <Button onClick={submitUpload} disabled={uploading} data-testid="upload-submit" className="tp-brand-btn">{uploading ? "Carico…" : "Carica PDF"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rinomina PDF */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm p-0 tp-glass-strong text-white border-white/10" data-testid="preventivo-rename-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
            <DialogTitle className="text-white flex items-center gap-2"><Pencil size={16} className="text-fuchsia-400" /> Rinomina</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-5">
            <input data-testid="rename-input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className={inputCls} placeholder="Nome file" autoFocus />
          </div>
          <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRenameOpen(false)} className="border-white/10 text-zinc-200">Annulla</Button>
            <Button onClick={submitRename} data-testid="rename-submit" className="tp-brand-btn">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Anteprima file PDF caricato */}
      <Dialog open={filePreviewOpen} onOpenChange={(o) => { setFilePreviewOpen(o); if (!o && filePreviewUrl) { URL.revokeObjectURL(filePreviewUrl); setFilePreviewUrl(null); } }}>
        <DialogContent className="max-w-[900px] p-0 bg-zinc-100 border-zinc-300" data-testid="preventivo-file-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10">
            <DialogTitle className="text-white flex items-center gap-2"><Eye size={18} className="text-fuchsia-400" /> Anteprima PDF</DialogTitle>
          </DialogHeader>
          <div className="p-3">
            {filePreviewUrl && <iframe title="pdf" src={filePreviewUrl} className="w-full h-[75vh] rounded-lg bg-white" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
